import _ from "lodash";

import { CarbonProgram, CarbonProgramDraftConfig } from "models";

// The draft form only edits the settings commercial specifies (the Notion request template and a bit more). Everything else
// in the config (practices aside, which are copied from a live program) is carried over untouched, so an update draft
// never silently drops settings the form doesn't show.

export const FOOTPRINTING_FORMS = ["farm", "crop", "dairy", "beef"] as const;
type FootprintingForm = (typeof FOOTPRINTING_FORMS)[number];

export type FormBonusRow = { year?: number | null } & Partial<Record<FootprintingForm, number | null>>;

export type DraftFormValues = Record<string, unknown> & {
  formBonusRows?: FormBonusRow[];
  practicesFromProgramId?: string;
};

// Value the platform uses when a setting is not configured. A form value equal to its default is not written into a
// config that didn't have it, so opening and saving an update draft doesn't show up as a change.
const PLATFORM_DEFAULTS: Record<string, unknown> = {
  noProgramTerms: false,
  verificationRequired: false,
  "platformFeatures.fieldManagement.enabled": false,
  "platformFeatures.fieldManagement.wallet": false,
  "platformFeatures.fieldManagement.outcomeBasedIncentives": false,
  "platformFeatures.fieldManagement.practiceBasedIncentives": false,
  "platformFeatures.fieldManagement.impactPanel": false,
  "platformFeatures.fieldManagement.insightsImpactPanel": false,
  "platformFeatures.fieldManagement.submission": false,
  "platformFeatures.footprinting.enabled": false,
  "platformFeatures.footprinting.results": true,
  "platformFeatures.footprinting.payouts": true,
  "platformFeatures.footprinting.dairy": false,
  "platformFeatures.footprinting.beef": false,
  "platformFeatures.footprinting.pork": false,
  "platformFeatures.planning.cropMixComposer": false,
};

export const SWITCH_PATHS = Object.keys(PLATFORM_DEFAULTS);

const VALUE_PATHS = [
  "programName",
  "internalName",
  "programSlug",
  "country",
  "currency",
  "accountingMethodology",
  "partnerId",
  "fieldsManagement.startYear",
  "fieldsManagement.methodology.socModel",
  "fieldsManagement.methodology.version",
  "fieldsManagement.fieldEnrollmentType",
  "fieldsManagement.reportingPeriod.start",
  "fieldsManagement.reportingPeriod.end",
  "fieldsManagement.submissionDates.start",
  "fieldsManagement.submissionDates.due",
  "fieldsManagement.submissionDates.end",
  "fieldsManagement.monitoringPeriodType",
  "fieldsManagement.payouts.removals.pointPrice",
  "fieldsManagement.payouts.avoidances.pointPrice",
  "fieldsManagement.payouts.reductions.pointPrice",
  "fieldsManagement.maximumPayoutPerHa",
  "fieldsManagement.programCrops",
  "fieldsManagement.projectArea.cropsOfInterest",
  "farmLevelFootprinting.programCrops",
  "farmLevelFootprinting.startYear",
  "farmLevelFootprinting.disabledYears",
  "farmLevelFootprinting.verificationStartYear",
  "farmLevelFootprinting.methodology.version",
  "farmLevelFootprinting.methodology.crops",
  "farmLevelFootprinting.methodology.dairy",
  "farmLevelFootprinting.methodology.beef",
  "farmLevelFootprinting.methodology.pork",
  "farmLevelFootprinting.formSubmissionBonuses.cropPayouts",
];

// Lists the schema requires even when empty; other empty lists are removed
const REQUIRED_LIST_PATHS = ["farmLevelFootprinting.programCrops"];

// Optional groups that are removed when none of their settings are filled in
const OPTIONAL_GROUP_PATHS = [
  "fieldsManagement.reportingPeriod",
  "fieldsManagement.submissionDates",
  "fieldsManagement.payouts",
  "fieldsManagement.projectArea",
  "farmLevelFootprinting.methodology",
];

const isBlank = (value: unknown) => value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0);

const hasFilledSetting = (value: unknown): boolean => (_.isPlainObject(value) ? Object.values(value as object).some(hasFilledSetting) : !isBlank(value));

export const BLANK_PROGRAM_CONFIG: CarbonProgramDraftConfig = {
  partnerId: null,
  platformFeatures: { fieldManagement: {}, footprinting: {}, planning: {} },
  fieldsManagement: { practices: [] },
  farmLevelFootprinting: { formSubmissionBonuses: { cropPayouts: "all-crops" }, programCrops: [], startYear: new Date().getFullYear() },
};

/** A copy of a live program as the starting point for a new one; the API gives the copy its own id */
export const copyOfProgram = (program: CarbonProgram): CarbonProgramDraftConfig => {
  const { id: _id, ...rest } = _.cloneDeep(program) as CarbonProgram & { farmCount?: number };
  delete rest.farmCount;
  return { ...rest, programName: `Copy of ${program.programName}`, internalName: undefined, programSlug: `${program.programSlug}-copy` };
};

export const configToFormValues = (config: CarbonProgramDraftConfig): DraftFormValues => {
  const values: DraftFormValues = {};
  [...VALUE_PATHS, ...SWITCH_PATHS].forEach((path) => _.set(values, path, _.get(config, path) ?? PLATFORM_DEFAULTS[path]));

  // selects can't hold null
  if (values.partnerId === null) values.partnerId = undefined;

  const bonuses = (_.get(config, "farmLevelFootprinting.formSubmissionBonuses") ?? {}) as Partial<Record<FootprintingForm, Record<string, number>>>;
  const years = _.uniq(FOOTPRINTING_FORMS.flatMap((form) => Object.keys(bonuses[form] ?? {}))).sort();
  values.formBonusRows = years.map((year) => ({
    year: Number(year),
    ...Object.fromEntries(FOOTPRINTING_FORMS.map((form) => [form, bonuses[form]?.[year] ?? null])),
  }));

  return values;
};

export const formValuesToConfig = (
  values: DraftFormValues,
  baseConfig: CarbonProgramDraftConfig,
  livePrograms: CarbonProgram[] = []
): CarbonProgramDraftConfig => {
  const config = _.cloneDeep(baseConfig);

  SWITCH_PATHS.forEach((path) => {
    const value = !!_.get(values, path);
    if (_.get(baseConfig, path) === undefined && value === PLATFORM_DEFAULTS[path]) return;
    _.set(config, path, value);
  });

  VALUE_PATHS.forEach((path) => {
    // fields the form doesn't show (e.g. inventory-only settings on an intervention program) keep their value
    if (!_.has(values, path)) return;
    const value = _.get(values, path);
    if (path === "partnerId") {
      _.set(config, path, value || null);
    } else if (isBlank(value)) {
      // an empty field leaves a deliberately empty setting (e.g. `maximumPayoutPerHa: null`, `programCrops: []`) as it was
      if (_.has(baseConfig, path) && isBlank(_.get(baseConfig, path))) return;
      if (REQUIRED_LIST_PATHS.includes(path)) _.set(config, path, []);
      else _.unset(config, path);
    } else {
      _.set(config, path, value);
    }
  });

  // years typed into a tags select arrive as strings
  const disabledYears = _.get(config, "farmLevelFootprinting.disabledYears");
  if (Array.isArray(disabledYears)) _.set(config, "farmLevelFootprinting.disabledYears", disabledYears.map(Number));

  OPTIONAL_GROUP_PATHS.forEach((path) => {
    const baseGroup = _.get(baseConfig, path);
    const groupWasAlreadyEmpty = baseGroup !== undefined && !hasFilledSetting(baseGroup);
    if (!hasFilledSetting(_.get(config, path)) && !groupWasAlreadyEmpty) _.unset(config, path);
  });

  const cropPayouts = _.get(config, "farmLevelFootprinting.formSubmissionBonuses.cropPayouts") ?? "all-crops";
  const bonuses: Record<string, unknown> = { cropPayouts };
  FOOTPRINTING_FORMS.forEach((form) => {
    const payoutsByYear = Object.fromEntries(
      (values.formBonusRows ?? []).filter((row) => !isBlank(row.year) && !isBlank(row[form])).map((row) => [String(row.year), row[form] as number])
    );
    if (!_.isEmpty(payoutsByYear)) bonuses[form] = payoutsByYear;
  });
  _.set(config, "farmLevelFootprinting.formSubmissionBonuses", bonuses);

  const practicesSource = livePrograms.find((program) => program.id === values.practicesFromProgramId);
  if (practicesSource) _.set(config, "fieldsManagement.practices", _.cloneDeep(practicesSource.fieldsManagement.practices));

  return config;
};

export type ConfigChange = { path: string; before: unknown; after: unknown };

const flattenSettings = (value: unknown, prefix = ""): Record<string, unknown> =>
  _.isPlainObject(value)
    ? Object.assign({}, ...Object.entries(value as object).map(([key, child]) => flattenSettings(child, prefix ? `${prefix}.${key}` : key)))
    : { [prefix]: value };

/** Every setting that differs between the live program and the draft; lists (crops, practices) compare as a whole */
export const diffConfigs = (live: unknown, draft: unknown): ConfigChange[] => {
  const before = flattenSettings(live);
  const after = flattenSettings(draft);

  return _.union(Object.keys(before), Object.keys(after))
    .filter((path) => !_.isEqual(before[path], after[path]))
    .sort()
    .map((path) => ({ path, before: before[path], after: after[path] }));
};
